import React, { useState, useEffect } from "react";
import axios from "axios";
import { getToken } from "utils/getToken";
import { formatToDateAndTime } from "utils/formatTime";
import { MdMessage } from "react-icons/md";
import { FaLongArrowAltRight } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import { ClipLoader } from "react-spinners";
import { HiExternalLink } from "react-icons/hi";
import IframeModal from "components/ChatStatus/IframeModal";
import { FiSearch } from "react-icons/fi";
import {
  ghostBtn,
  inlineLink,
  input as inputCls,
  microLabel,
  primaryBtn,
} from "components/ui/instrument";

interface UserMessagesProps {
  mobileNo?: string;
  userId?: number | string;
}

const UserMessages = ({ mobileNo, userId }: UserMessagesProps) => {
  const [currPage, setCurrPage] = useState(1);
  const [currData, setCurrData] = useState<any>({});
  const [resultsArray, setResultsArray] = useState<any[]>([]);
  const [view, setView] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalContent, setModalContent] = useState<{ url: string } | null>(null);
  const [filterPage, setFilterPage] = useState<number | string>(1);
  const [error, setError] = useState<string | null>(null); // New error state

  const fetchData = async (mobileNo?: string, userId?: number | string) => {
    try {
      setLoading(true);
      setError(null); // Reset error state when fetching data
      const dataForCurrPage = await axios.post(
        `${process.env.REACT_APP_API_URL}users/paginated-whatsapp-message/?page=${currPage}`,
        mobileNo
          ? JSON.stringify({
              mobile: `${mobileNo}`,
            })
          : userId
          ? JSON.stringify({
              user_id: userId,
            })
          : null,
        {
          headers: {
            Authorization: `Token ${getToken()}`,
            "Content-Type": "application/json",
          },
        }
      );
      setCurrData(dataForCurrPage.data);
      setResultsArray(dataForCurrPage.data.results);
    } catch (error) {
      toast.error("Some error occurred!");
      setError("Failed to load messages. Please try again");
    } finally {
      setLoading(false); // Stop loader in both success and failure
    }
  };

  const totalPages = Math.ceil(currData.count / 50);

  useEffect(() => {
    if (view) fetchData(mobileNo, userId);
  }, [currPage, mobileNo, userId, view]);

  const handleNextPage = () => {
    setCurrPage((prevPage) => prevPage + 1);
    window.scrollTo(0, 0);
  };

  const handlePrevPage = () => {
    setCurrPage((prevPage) => prevPage - 1);
    window.scrollTo(0, 0);
  };

  function createLinkAndOpenChat(row: any, rowIndex: number, mobileNum: any) {
    const chatStartTime = new Date(row.user_message.time);
    const startTimeOffset = chatStartTime.getTimezoneOffset();

    const lastMessageTime = new Date(row.user_message.time);
    const lastMessageTimeOffset = lastMessageTime.getTimezoneOffset();
    const startTime = new Date(
      chatStartTime.getTime() - 10 * 60 * 1000 - startTimeOffset * 60 * 1000
    );
    const endTime = new Date(
      lastMessageTime.getTime() +
        10 * 60 * 1000 -
        lastMessageTimeOffset * 60 * 1000
    );

    const startTimeISO = startTime.toISOString().slice(0, 19);
    const endTimeISO = endTime.toISOString().slice(0, 19);

    const mobileNumTransformed = mobileNum.split("+")[1];

    const url = `/whatsapp-chat?group_id=${row.user_message.group_id}&start_time=${startTimeISO}&end_time=${endTimeISO}&mobile_number=${mobileNumTransformed}`;
    setModalContent({ url });
  }

  const closeModal = () => {
    setModalContent(null);
  };

  const handlePageSearch = () => {
    const pageNo = parseInt(filterPage as string, 10);
    if (!isNaN(pageNo) && pageNo <= totalPages) {
      setCurrPage(pageNo);
    } else {
      toast.error("Please enter a valid page number!");
    }
  };

  const handleTryAgain = () => {
    fetchData(mobileNo, userId);
  };

  return (
    <>
      <p
        onClick={() => setView(true)}
        className="flex justify-items-stretch text-center text-clinic-700"
      >
        {!view && (
          <button className={`w-full ${primaryBtn}`}>
            View Whatsapp Chat
          </button>
        )}
      </p>

      {modalContent && (
        <IframeModal onClose={closeModal} url={modalContent.url} />
      )}

      {view && (
        <div
          className={`h-auto w-full overflow-y-auto rounded-lg border border-clinic-200 bg-white p-3 shadow-sm`}
        >
          {!loading ? (
            error ? (
              <div className="py-6 text-center text-sm text-crit-600">
                {error}
                <button
                  className={`mx-auto mt-4 block ${primaryBtn}`}
                  onClick={handleTryAgain}
                >
                  Try Again
                </button>
              </div>
            ) : (
              <div
                className={`overflow-y-scroll ${
                  resultsArray.length > 0 ? "h-80" : ""
                }`}
              >
                {/* messages top bar */}
                <div className="sticky -top-2 z-40 bg-white p-1 ">
                  <div className="flex items-center gap-1.5 text-clinic-500">
                    <MdMessage className="text-lg" />
                    <p>
                      <b>Total Messages</b> :{" "}
                      {currData.count ? currData.count : null}
                    </p>
                  </div>

                  <div className="mt-2 flex w-full gap-10 p-0">
                    <h4 className={`w-1/2 ${microLabel}`}>Messages</h4>
                    <h4 className={`w-1/2 ${microLabel}`}>Replies</h4>
                  </div>
                </div>
                {/* border */}
                <p className="border-b border-clinic-200"></p>
                {/* --------------- Message Container ---------- */}
                <div className="">
                  {resultsArray.length > 0 ? (
                    resultsArray.map((message, index) => (
                      <div
                        key={index}
                        className="my-2 flex max-h-60 w-full items-center gap-10 rounded-md "
                      >
                        {/* left portion  (user message container) */}
                        <div className="h-full w-1/2 rounded-md">
                          <div
                            className={`relative my-0 mx-0 rounded-md border border-clinic-200 p-2 text-sm text-clinic-700 ${
                              message.reply.length > 0
                                ? "bg-clinic-50"
                                : "bg-white"
                            }`}
                          >
                            {/* user message msg */}
                            <p>{message.user_message.message}</p>
                            {/* time */}
                            <div className="text-right text-xs tabular-nums text-clinic-500 ">
                              {" "}
                              <div className="flex items-center gap-4">
                                <p>
                                  {formatToDateAndTime(
                                    message.user_message.time
                                  )}
                                </p>

                                <button
                                  className={`flex items-center gap-1 ${inlineLink}`}
                                  onClick={() => {
                                    createLinkAndOpenChat(
                                      message,
                                      index,
                                      mobileNo
                                    );
                                  }}
                                >
                                  Open chat
                                  <HiExternalLink />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                        {message.reply.length > 0 && (
                          <>
                            <FaLongArrowAltRight className="shrink-0 text-clinic-400" />
                            <div className="h-full w-1/2 rounded-md">
                              {message.reply.map((reply: any, replyIndex: number) => (
                                <div
                                  key={replyIndex}
                                  className="relative mx-2 my-2 rounded-md border border-clinic-200 bg-white p-2 text-sm text-clinic-700"
                                >
                                  {/* msg */}
                                  <p>{reply.message}</p>
                                  {/* time */}
                                  <p className="text-right text-xs tabular-nums text-clinic-500 ">
                                    {formatToDateAndTime(reply.time)}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="px-5 py-10 text-center text-sm text-clinic-500">
                      No message found
                    </div>
                  )}
                </div>
              </div>
            )
          ) : (
            <>
              <ClipLoader
                color={"#94a3b8"}
                size={100}
                className="relative left-2/4 top-1/2 h-10 w-full"
              />
            </>
          )}
          <div className="mt-4 flex flex-col justify-center md:mt-6 md:flex-row">
            <div className="flex justify-center md:ml-auto">
              <button
                onClick={handlePrevPage}
                disabled={currData.previous === null || currPage === 1}
                className={`flex items-center gap-1 ${ghostBtn}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="inline-flex h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M9.707 4.293a1 1 0 010 1.414L6.414 9H16a1 1 0 110 2H6.414l3.293 3.293a1 1 0 01-1.414 1.414l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Previous Page
              </button>
              <h3 className="flex items-center px-2 text-sm tabular-nums text-clinic-700 ">
                Page {currPage} of {totalPages}
              </h3>

              <button
                onClick={handleNextPage}
                disabled={currData.next == null || currPage === totalPages}
                className={`flex items-center gap-1 ${ghostBtn}`}
              >
                Next Page
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="inline-flex h-5 w-5 -rotate-180 transform"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M9.707 4.293a1 1 0 010 1.414L6.414 9H16a1 1 0 110 2H6.414l3.293 3.293a1 1 0 01-1.414 1.414l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
            <div className="mt-5 flex items-center justify-end md:ml-auto md:mt-0">
              <input
                type="text"
                placeholder={`Go to page (1 - ${totalPages})`}
                value={filterPage}
                onChange={(e) => setFilterPage(e.target.value)}
                className={`tabular-nums ${inputCls}`}
              />
              <button
                aria-label="Go to page"
                className={`mx-2 ${primaryBtn}`}
                onClick={handlePageSearch}
              >
                <FiSearch className="h-4 w-4" />
              </button>
            </div>
          </div>
          <ToastContainer
            position="bottom-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick={false}
            rtl={false}
            pauseOnFocusLoss={false}
            draggable
            pauseOnHover
            theme="light"
          />
        </div>
      )}
    </>
  );
};

export default UserMessages;
